import React, { useState } from 'react';
import { useRecoilValue } from 'recoil';
import { network } from 'recoil/atom/network';
import { CustomUpload } from './styled';
import { InboxOutlined } from '@ant-design/icons';
import { Form } from 'antd';
import {
  getPreSignedUrlToUploadOrDownload,
  uploadFileToS3WithPreSignedUrl,
} from 'api/storage/storage.api';
import FormMessage from 'components/Form/FormMessage/index';
import {
  NOTIFICATION_FILE_EXTENSION,
  NOTIFICATION_FILE_TYPE,
} from 'constant/notificationConstants';
import useCustomMutate from 'hook/useQuery/useCustomMutate';
import useCustomQuery from 'hook/useQuery/useCustomQuery';
import useUploadFileMultiLanguage from 'hook/useUploadFileMultiLanguage';

const FormFileUpload = ({
  name,
  multipleUpload,
  isDisabled,
  maxCount,
  text,
  form,
  setIdFile,
  hint,
  setIsDisabled,
  fileObject,
  setFileObject,
}) => {
  const [isShowFile, setIsShowFile] = useState(true);
  const { useQueryAPI } = useCustomQuery();
  const { useMutationAPI } = useCustomMutate();
  const status = useRecoilValue(network);
  const {
    UPLOAD_FILE_SUCCESS,
    UPLOAD_FILE_FAILURE,
    UPLOAD_FILE_ALLOWED_EXTENSION,
    UPLOAD_FILE_MAX_SIZE,
    UPLOAD_FILE_REQUIRED,
    UPLOAD_FILE_WITHOUT_INTERNET,
  } = useUploadFileMultiLanguage();

  const { refetch: refetchPreSignedUrl } = useQueryAPI({
    api: getPreSignedUrlToUploadOrDownload,
    params: {
      fileName: fileObject?.keyFileName,
      httpMethod: 'PUT',
    },
    options: {
      refetchOnWindowFocus: false,
    },
    enabled: !!fileObject?.keyFileName,
  });

  const { mutateAsync: mutateAsyncUpload } = useMutationAPI({
    api: uploadFileToS3WithPreSignedUrl,
    messageSuccess: UPLOAD_FILE_SUCCESS,
    messageError: UPLOAD_FILE_FAILURE,
  });

  const beforeUpload = (file) => {
    const fileType = NOTIFICATION_FILE_TYPE[file.type];
    const fileExtension =
      NOTIFICATION_FILE_EXTENSION[file.name?.split('.')?.pop().toLowerCase()];
    if (!status) {
      return false;
    }
    if (file) {
      if (!fileType && !fileExtension) {
        return false;
      } else {
        if (fileType) {
          if (file.size > fileType.size) {
            return false;
          }
        } else if (fileExtension) {
          if (file.size > fileExtension.size) {
            return false;
          }
        }
      }
    }
    return true;
  };

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    setIdFile(null);
    return e?.fileList;
  };

  const options = (onProgress, file) => ({
    onUploadProgress: (progress) => {
      const { loaded, total } = progress;
      onProgress(
        {
          percent: Math.round((loaded / total) * 100),
        },
        file,
      );
    },
    headers: {
      'Content-Type': file?.type,
    },
  });

  return (
    <Form.Item
      name={name}
      valuePropName={'fileList'}
      getValueFromEvent={normFile}
      required={true}
      rules={[
        {
          validator: (_, value) => {
            if (status || !status) {
              if (
                value === undefined ||
                value === [] ||
                value[0] === undefined
              ) {
                setIsShowFile(true);
                return Promise.reject(
                  <FormMessage
                    text1={UPLOAD_FILE_REQUIRED}
                    marginBottom={'12px'}
                  />,
                );
              }
            }

            if (!status && value) {
              form.setFieldValue('fileResponse', []);
              setIsShowFile(false);
              return Promise.reject(
                <FormMessage
                  text1={UPLOAD_FILE_WITHOUT_INTERNET}
                  marginBottom={'12px'}
                />,
              );
            }

            if (value && value.length > 0) {
              const fileType = NOTIFICATION_FILE_TYPE[value[0].type];
              const fileExtension =
                NOTIFICATION_FILE_EXTENSION[
                  value[0].name?.split('.')?.pop().toLowerCase()
                ];
              if (!fileType && !fileExtension) {
                form.setFieldValue('fileResponse', []);
                setIsShowFile(false);
                return Promise.reject(
                  <FormMessage
                    text1={UPLOAD_FILE_ALLOWED_EXTENSION}
                    marginBottom={'12px'}
                  />,
                );
              } else {
                let sizeError = false;
                if (fileType) {
                  if (value[0].size > fileType.size) {
                    sizeError = true;
                  }
                } else if (fileExtension) {
                  if (value[0].size > fileExtension.size) {
                    sizeError = true;
                  }
                }
                if (sizeError === true) {
                  form.setFieldValue('fileResponse', []);
                  setIsShowFile(false);
                  return Promise.reject(
                    <FormMessage
                      text1={UPLOAD_FILE_MAX_SIZE}
                      marginBottom={'12px'}
                    />,
                  );
                }
              }
            }
            setIsShowFile(true);
            return Promise.resolve();
          },
        },
      ]}
    >
      <CustomUpload
        multipleUpload={multipleUpload}
        disabled={isDisabled}
        maxCount={maxCount}
        showUploadList={isShowFile}
        progress={{
          strokeColor: {
            '0%': '#108ee9',
            '100%': '#87d068',
          },
          size: 'small',
          format: (percent) => percent && `${parseFloat(percent.toFixed(2))}%`,
        }}
        beforeUpload={beforeUpload}
        customRequest={async ({ file, onError, onSuccess, onProgress }) => {
          setIsDisabled(true);

          const extension = file?.name?.substring(
            file?.name?.lastIndexOf('.'),
            file?.name?.length,
          );

          const modifiedFileName = file?.name?.replace(
            extension,
            extension?.toLowerCase(),
          );

          await setFileObject({
            fileName: modifiedFileName,
            keyFileName: file?.uid,
            size: file?.size,
            contentType: file?.type,
          });

          refetchPreSignedUrl().then((value) => {
            mutateAsyncUpload({
              url: value?.data,
              file: file,
              options: options(onProgress, file),
            })
              .then((value) => {
                onSuccess(value, file);
                setIsDisabled(false);
              })
              .catch((error) => {
                onError(error);
                setIsDisabled(false);
              });
          });
        }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text" style={{ padding: '0 13%' }}>
          {text}
        </p>
        <p
          className="ant-upload-hint"
          style={{
            color: '#656464',
            fontFamily: 'Roboto',
            padding: '0 15%',
          }}
        >
          {hint}
        </p>
      </CustomUpload>
    </Form.Item>
  );
};

export default FormFileUpload;
